/* ==========================================================================
   POSSEN — desktop mega navigation: behaviour

   One panel open at a time, driven by an `is-open` class on its <li>; all
   motion is CSS (mega-nav.css). Timing is the whole design here:

     OPEN_DELAY   120ms before the FIRST panel opens. Long enough that a
                  pointer crossing the row on its way to the logo, the
                  search or the page doesn't flash a panel; short enough to
                  feel immediate to someone who meant it.
     SWITCH_DELAY  70ms to move to another category once one is open, with
                  no animation. A diagonal path from "Suits" down to its
                  Weavers column crosses Jackets and Outerwear in well under
                  that, so it doesn't hijack the panel on the way.
     CLOSE_DELAY  220ms grace after leaving, so overshooting the panel edge
                  by a few pixels doesn't throw it away.

   Also: keyboard (disclosure buttons, Escape, focus leaving closes), touch
   on desktop-width tablets (first tap opens, second follows the link), the
   header is held visible while a panel is open, and the panel closes on a
   deliberate scroll, a resize below desktop, or when the search or the
   row toggle is used.
   ========================================================================== */

(function () {
  'use strict';

  var OPEN_DELAY = 120;
  var SWITCH_DELAY = 70;
  var CLOSE_DELAY = 220;
  var SCROLL_CLOSE = 80;

  var desktop = window.matchMedia('(min-width: 60.0625rem)');
  var items = Array.prototype.slice.call(document.querySelectorAll('[data-mega-item]'));
  var nav = document.querySelector('.mainNavigationDiv');
  var header = document.querySelector('header.u-full-width');
  if (!items.length || !nav || !header) return;

  var root = document.documentElement;
  var openItem = null;
  var timer = null;
  var headerLocked = false;
  var openedAtY = 0;
  var lastPointerType = 'mouse';

  /* The scrim sits beside the header, not inside it: see mega-nav.css. */
  var scrim = document.createElement('div');
  scrim.className = 'MegaNav__scrim';
  scrim.setAttribute('aria-hidden', 'true');
  header.parentNode.insertBefore(scrim, header.nextSibling);

  function toggleOf(item) {
    return item.querySelector('[data-mega-toggle]');
  }

  /* Span the viewport exactly from wherever the nav sits. Written once per
     open (and on resize), never per frame. */
  function place() {
    var r = nav.getBoundingClientRect();
    nav.style.setProperty('--mega-left', -r.left + 'px');
    nav.style.setProperty('--mega-width', root.clientWidth + 'px');
  }

  function lockHeader(on) {
    if (on === headerLocked || !window.PossenHeader) return;
    headerLocked = on;
    if (on) window.PossenHeader.lock();
    else window.PossenHeader.unlock();
  }

  function clearTimer() {
    window.clearTimeout(timer);
    timer = null;
  }

  function schedule(fn, ms) {
    clearTimer();
    timer = window.setTimeout(fn, ms);
  }

  /* `instant`: keyboard-initiated opens don't animate — a key press should
     feel like it acted, not like it started something. */
  function setOpen(item, instant) {
    clearTimer();
    if (item === openItem) return;

    var switching = !!(openItem && item) || !!instant;
    if (switching) {
      root.setAttribute('data-mega-switching', '');
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          root.removeAttribute('data-mega-switching');
        });
      });
    }

    if (openItem) {
      openItem.classList.remove('is-open');
      var t = toggleOf(openItem);
      if (t) t.setAttribute('aria-expanded', 'false');
    }

    openItem = item;

    if (item) {
      place();
      item.classList.add('is-open');
      var t2 = toggleOf(item);
      if (t2) t2.setAttribute('aria-expanded', 'true');
      scrim.classList.add('is-visible');
      lockHeader(true);
      openedAtY = window.pageYOffset;
    } else {
      scrim.classList.remove('is-visible');
      lockHeader(false);
    }
  }

  function close() {
    setOpen(null);
  }

  items.forEach(function (item) {
    var toggle = toggleOf(item);
    var link = item.querySelector('a.top-level');

    /* Pointer: intent-delayed open, quick switch, graceful close. */
    item.addEventListener('pointerenter', function (e) {
      lastPointerType = e.pointerType;
      if (!desktop.matches || e.pointerType === 'touch') return;
      if (item === openItem) {
        clearTimer();
        return;
      }
      schedule(function () { setOpen(item); }, openItem ? SWITCH_DELAY : OPEN_DELAY);
    });

    item.addEventListener('pointerleave', function (e) {
      if (!desktop.matches || e.pointerType === 'touch') return;
      if (openItem) schedule(close, CLOSE_DELAY);
      else clearTimer();
    });

    item.addEventListener('pointerdown', function (e) {
      lastPointerType = e.pointerType;
    });

    /* Touch on a desktop-width screen has no hover: the first tap on a
       category opens its panel, the second follows the link. Captured on
       the <li> so it runs before appupdate.js's jQuery handler on the link,
       which would otherwise navigate away. */
    if (link) {
      item.addEventListener('click', function (e) {
        if (!desktop.matches || lastPointerType !== 'touch') return;
        if (e.target.closest('a.top-level') !== link) return;
        if (openItem === item) return;
        e.preventDefault();
        e.stopPropagation();
        setOpen(item);
      }, true);
    }

    /* Keyboard: the disclosure button opens and closes without delay. */
    if (toggle) {
      toggle.addEventListener('click', function () {
        setOpen(openItem === item ? null : item, true);
      });
    }

    /* Focus leaving the category (and its panel) closes it. */
    item.addEventListener('focusout', function (e) {
      if (openItem !== item) return;
      if (e.relatedTarget && item.contains(e.relatedTarget)) return;
      if (!e.relatedTarget) return; /* window blur, or a click on nothing */
      close();
    });

    /* "+N more" reveals the rest of a group in place. */
    item.addEventListener('click', function (e) {
      var more = e.target.closest('[data-mega-more]');
      if (!more) return;
      var group = more.closest('.MegaPanel__group');
      var expanded = group.classList.toggle('is-expanded');
      more.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      more.textContent = more.getAttribute(expanded ? 'data-label-open' : 'data-label-closed');
    });
  });

  /* Leaving onto the page (through the scrim) closes; so does a click. */
  scrim.addEventListener('pointerenter', function () {
    if (openItem) schedule(close, CLOSE_DELAY);
  });
  scrim.addEventListener('click', close);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !openItem) return;
    var item = openItem;
    var focusInside = item.contains(document.activeElement);
    close();
    if (focusInside) {
      var t = toggleOf(item);
      if (t) t.focus();
    }
  });

  window.addEventListener('scroll', function () {
    if (openItem && Math.abs(window.pageYOffset - openedAtY) > SCROLL_CLOSE) close();
  }, { passive: true });

  window.addEventListener('resize', function () {
    if (!openItem) return;
    if (!desktop.matches) close();
    else place();
  });

  window.addEventListener('blur', function () {
    clearTimer();
  });

  /* Other header controls take over the space below the header. */
  document.addEventListener('click', function (e) {
    if (!openItem) return;
    if (e.target.closest('[data-hs-toggle], [data-main-nav-toggle]')) close();
  }, true);
})();
